// interactionEdit.ts
//
// The rules and the state behind "Edit interactions" (plan 2026-10-05
// dashboard interactivity, B5). Pure helpers first, so the toggle rules are
// tested without mounting the page; the hook last.
//
// A tile is "filtered by" the source when the source's targets are "auto"
// and the tile can take one of the source's click dimensions, or when its
// block key is on the source's list. The first switch an author flips turns
// "auto" into the explicit list it stood for, minus or plus that tile, so
// nothing else changes under them.
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { notifications } from "@mantine/notifications";
import { ApiError } from "@/api/client";
import { updateItemInteractions, type DashboardItemDTO, type InteractionTargets } from "@/api/dashboards";
import type { DashboardInteractionEditContextValue, InteractionEdit } from "@/context/DashboardInteractionEditContext";
import { interactionTargetsOf } from "@/utils/tabFilters";
import {
  blockTypeOfSpec,
  isDataBlockType,
  isPlaceholderSpec,
  openFieldControlOfSpec,
} from "@/components/dashboards/composition/blocks";
import { dateRangeControlOfSpec } from "@/components/dashboards/composition/openControlSpec";
import { SETTINGS_COPY } from "@/components/dashboards/composition/settingsCopy";
import { hasOwnPeriod, isDateControlReachable, isPeriodTile, PERIOD_DIMENSION } from "@/utils/dateControlPeriods";

type Dim = { key: string; label: string };

/** The dimensions a click on `item` filters on: the viewer-filterable ones
 *  it draws (its index, its split, its pivot rows), else every filterable
 *  one when the spec names none. An open field control (ADR-0350) has no
 *  data of its own: it "clicks" on the one field it steers, so the same
 *  toggles narrow it (none until the author picked a field). Its label is
 *  the FIELD's, read off the page's tiles (`pageItems`): the control's own
 *  label names the control, and its key is not for a reader. */
export function clickDimensionsOf(
  item: Pick<DashboardItemDTO, "filterable_dimensions" | "chart_spec_json">,
  pageItems: ReadonlyArray<Pick<DashboardItemDTO, "filterable_dimensions">> = [],
): Dim[] {
  // A date range control (controls A4b) steers the period: every tile with
  // one can take it (`canTake`).
  if (dateRangeControlOfSpec(item.chart_spec_json ?? null)) return [{ ...PERIOD_DIMENSION }];
  const control = openFieldControlOfSpec(item.chart_spec_json ?? null);
  if (control) {
    const key = control.dimension;
    if (!key) return [];
    const label = pageItems.flatMap((i) => i.filterable_dimensions ?? []).find((d) => d.key === key)?.label;
    return [{ key, label: label || key }];
  }
  const filterable = item.filterable_dimensions ?? [];
  const spec = (item.chart_spec_json ?? {}) as Record<string, unknown>;
  const drawn = new Set<string>();
  for (const key of [spec.chart_index_by, spec.breakdown_key]) if (typeof key === "string") drawn.add(key);
  if (Array.isArray(spec.dimension_keys)) for (const key of spec.dimension_keys) if (typeof key === "string") drawn.add(key);
  const picked = filterable.filter((d) => drawn.has(d.key));
  return picked.length ? picked : filterable;
}

/** What the edit is about, for a reader. An open control: its own label,
 *  else its field's label (its tile name is the generic "Control"). A chart:
 *  its name. */
export function interactionSourceName(editing: Pick<InteractionEdit, "source" | "clickDimensions">): {
  name: string;
  isControl: boolean;
} {
  const control = openFieldControlOfSpec(editing.source.chart_spec_json ?? null);
  if (control) {
    return { name: control.label ?? editing.clickDimensions[0]?.label ?? "this control", isControl: true };
  }
  const dateRange = dateRangeControlOfSpec(editing.source.chart_spec_json ?? null);
  if (dateRange) return { name: dateRange.label ?? "this date range", isControl: true };
  return { name: editing.source.name?.trim() || "this tile", isControl: false };
}

type Target = Pick<DashboardItemDTO, "filterable_dimensions">
  & Partial<Pick<DashboardItemDTO, "chart_spec_json" | "query_ir_json" | "query_sql" | "period">>;

/** Why a date range control cannot take a data tile that carries a period of
 *  its own (ADR-0445 decision 5: fixed is fixed). */
export const HAS_OWN_PERIOD_REASON = SETTINGS_COPY.appliesTo.hasOwnPeriod;

/** Can a click filter on one of `dims` reach this tile at all? A date range
 *  control reaches the data tiles without a period of their own. */
export function canTake(target: Target, dims: readonly Dim[]): boolean {
  if (dims.some((d) => d.key === PERIOD_DIMENSION.key)) return isDateControlReachable(target);
  const reach = new Set((target.filterable_dimensions ?? []).map((d) => d.key));
  return dims.some((d) => reach.has(d.key));
}

/** Is this a tile the source could reach but for a period of its own: it is
 *  listed, disabled, with `HAS_OWN_PERIOD_REASON` (a date range control as
 *  the source only). */
export function blockedByOwnPeriod(target: Target, dims: readonly Dim[]): boolean {
  return dims.some((d) => d.key === PERIOD_DIMENSION.key) && isPeriodTile(target) && hasOwnPeriod(target);
}

/** Is this a tile the toggles are drawn on: a configured data tile. */
export function isInteractionCandidate(item: Pick<DashboardItemDTO, "chart_spec_json">): boolean {
  const spec = item.chart_spec_json ?? null;
  return !isPlaceholderSpec(spec) && isDataBlockType(blockTypeOfSpec(spec));
}

/** Can a click on this tile filter the page at all: a chart or a table (a
 *  bar, a slice, a row), or an open field control. A KPI (scorecard) draws
 *  one number with nothing to click on, so it is a TARGET only: it gets no
 *  Interactions section and no Edit interactions entry. */
export function isClickSource(item: Pick<DashboardItemDTO, "chart_spec_json">): boolean {
  const spec = item.chart_spec_json ?? null;
  if (openFieldControlOfSpec(spec) || dateRangeControlOfSpec(spec)) return true;
  if (isPlaceholderSpec(spec)) return false;
  const type = blockTypeOfSpec(spec);
  return type === "chart" || type === "table";
}

/** The tiles on the tab that the toggles are drawn on: every other data tile. */
export function candidateTargets(items: readonly DashboardItemDTO[], sourceId: string): DashboardItemDTO[] {
  return items.filter((i) => i.id !== sourceId && isInteractionCandidate(i));
}

export function isTargeted(target: DashboardItemDTO, targets: InteractionTargets, dims: readonly Dim[]): boolean {
  if (!canTake(target, dims)) return false;
  return targets === "auto" ? true : target.block_key !== undefined && targets.includes(target.block_key);
}

/** The list stored after switching `target` on or off. */
export function nextTargets(
  items: readonly DashboardItemDTO[],
  sourceId: string,
  targets: InteractionTargets,
  dims: readonly Dim[],
  target: DashboardItemDTO,
  on: boolean,
): string[] {
  const base =
    targets === "auto"
      ? candidateTargets(items, sourceId).filter((i) => canTake(i, dims)).map((i) => i.block_key)
      : [...targets];
  const keys = base.filter((k): k is string => typeof k === "string" && k !== target.block_key);
  return on && target.block_key ? [...keys, target.block_key] : keys;
}

/**
 * The page's half: which tile is being scoped, and the writes. `items` is
 * the active tab's; `enabled` is false outside edit mode or without the
 * right to scope (the edit then closes).
 */
export function useInteractionEditor({
  items,
  enabled,
  onPatched,
}: {
  items: readonly DashboardItemDTO[];
  enabled: boolean;
  onPatched: (saved: DashboardItemDTO) => void;
}): DashboardInteractionEditContextValue | null {
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const source = sourceId ? items.find((i) => i.id === sourceId) ?? null : null;
  // Leaving edit mode, switching tab or deleting the source ends the edit.
  // Adjusted during render (React's pattern for state derived from props),
  // so no frame draws a stale edit.
  if (sourceId && (!enabled || !source)) setSourceId(null);

  // The page's handler is a new function every render; read it through a ref
  // so the context value does not change (and re-render every tile) with it.
  const onPatchedRef = useRef(onPatched);
  useLayoutEffect(() => {
    onPatchedRef.current = onPatched;
  });
  const write = useCallback(
    (item: DashboardItemDTO, targets: InteractionTargets) => {
      setSaving(true);
      updateItemInteractions(item.id, targets).then(
        (saved) => {
          onPatchedRef.current(saved);
          setSaving(false);
        },
        (err: unknown) => {
          setSaving(false);
          // A refusal with a reason (a date range control reaching a chart
          // another one already sets, controls A4b) says that reason. The
          // BFF forwards a backend 422 as `{error: {message}}`, so the reason
          // is the ApiError's message; a direct `detail` wins when present.
          const reason =
            err instanceof ApiError && err.status === 422
              ? (typeof err.detail === "string" && err.detail) || err.message || null
              : null;
          notifications.show({
            color: "red",
            title: "Interactions not saved",
            message: reason ?? `Which tiles "${item.name ?? "this tile"}" filters could not be saved. Nothing changed.`,
          });
        },
      );
    },
    [],
  );

  const editing = useMemo(
    () =>
      source
        ? {
            source,
            targets: interactionTargetsOf(source.chart_spec_json),
            clickDimensions: clickDimensionsOf(source, items),
            saving,
          }
        : null,
    [source, saving, items],
  );

  return useMemo(() => {
    if (!enabled) return null;
    return {
      editing,
      start: (itemId: string) => setSourceId(itemId),
      stop: () => setSourceId(null),
      setTargeted: (target: DashboardItemDTO, on: boolean) => {
        if (!editing || editing.saving) return;
        write(
          editing.source,
          nextTargets(items, editing.source.id, editing.targets, editing.clickDimensions, target, on),
        );
      },
      reset: () => {
        if (editing && !editing.saving && editing.targets !== "auto") write(editing.source, "auto");
      },
    };
  }, [enabled, editing, items, write]);
}
